/**
 * @citeclerk/plan — THE canonical launch pricing + entitlement table.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * WHY THIS PACKAGE EXISTS. Before it there were FOUR tables and they disagreed:
 *
 *   1. apps/main/server/lib/plan.js       PLAN_LIMITS { citations, checkCite, history, clark }
 *   2. apps/steel/server/lib/entitlement.js PLAN_LIMITS { citations, checkcite, searches, matters,
 *                                                         draftsPerMatter, clark }
 *   3. apps/steel/server/lib/plans.js     PLANS keyed on slugs THAT NO LONGER EXIST
 *                                         (student1/student2/attorney1/attorney2) — so
 *                                         usage.js:67's checkLimit(plan, …) fed it a real slug
 *                                         ('attorney'), missed, and silently fell through to
 *                                         PLANS.free for EVERY PAYING USER. Inert only because
 *                                         STEEL_ENFORCE_LIMITS is unset.
 *   4. Clerk's own commerce plans (the prices customers actually pay).
 *
 * (1) and (2) were kept in sync BY HAND, and the header of each said so. They drifted anyway —
 * free.citations was 20 in one and 30 in the other for long enough that closing the drift needed a
 * written ruling. Hand-syncing two tables is not a mechanism; it is a promise. This is the mechanism.
 *
 * 🔴 AND THE MECHANISM SHIPPED CARRYING THE LOSING NUMBER. RUN F, 2026-08-12.
 * ADAM'S RULING 2026-08-05 — commit 4691cf7d, "B1c: free citations = 30": *"FREE CITATIONS 20 -> 30
 * in apps/main/server/lib/plan.js, closing the drift on STEEL's canonical number. main was the one
 * that was wrong."* The ruling is also quoted verbatim in apps/main/server/lib/plan.js:21-24.
 * This file was created SIX DAYS LATER (b5040dba, 2026-08-11), cited that exact drift in its commit
 * message — "free.citations 20 vs 30" — and then wrote **20**, the number the ruling had already
 * overturned. So the consolidation that existed to end the drift re-introduced it, one table over,
 * and put it in the table every other table is supposed to defer to. It is corrected to 30 here.
 *
 * The direction is what made this urgent: both APP tables carried the ruled value, so a consolidation
 * that unified on "the canonical value" would have SILENTLY CUT every free user from 30 to 20.
 *
 * 🔴 AND THEN THE FOUNDER REVERSED IT. ADAM'S RULING 2026-08-15 — free.citations IS 20.
 * This is a DELIBERATE REVERSAL of the 2026-08-05 ruling quoted above, made with the whole table in
 * front of him while rewriting the pricing page, and it is recorded here at the same volume as the
 * ruling it overturns so that the next run does not read the paragraph above, conclude "30 is the
 * ruled value, 20 is the old drift", and "fix" it back. It is not drift. The newer ruling wins.
 *
 *   free.citations           30 → 20    reverses the 2026-08-05 ruling (4691cf7d), on purpose.
 *   student_ultra.citations 200 → 250    settles the ONE cell RUN F/RUN H left open. Canonical said
 *                                        200; both app tables, the live pricing page and the
 *                                        checkout screen all said 250. 250 wins — nothing sold is
 *                                        cut, and the quarantine in tableParity.test.mjs closes.
 *   student_ultra.drafts      1 → 2      the pricing page now sells "2 brief drafts/month".
 *                                        🔴 REVERSED 2026-08-28 — see the block below.
 *
 * 🔴 THE APP TABLES STILL SAY 30 AND THIS RUN DID NOT TOUCH THEM. apps/main/server/lib/plan.js:43
 * and apps/steel/server/lib/entitlement.js:63 both carry free.citations: 30 — they are ENFORCEMENT
 * and moving them is not a copy run's call. So for the free tier the page now promises 20 and the
 * two gates still grant 30: the customer gets MORE than advertised, which is the safe direction of
 * that gap but is still a gap. It is quarantined with both values in tableParity.test.mjs and is the
 * next thing to close.
 *
 * 🔴 AND STUDENT ULTRA'S DRAFTS WENT BACK TO ONE. ADAM'S RULING 2026-08-28 — drafts IS 1.
 * Adam read the LIVE pricing page, saw "2 brief drafts/month" on Student Ultra, and ruled it should
 * be one. This REVERSES the 1 → 2 raise recorded above, and it is written here at the same volume
 * as that raise for the same reason the free.citations paragraph is: so the next run does not read
 * the line above, conclude "2 is the ruled value", and put it back.
 *
 * 🔴 AND THE 1 → 2 RAISE NEVER HAD A RULING BEHIND IT — THE CITATION WAS CIRCULAR. Read its own
 * justification again: *"the pricing page now sells '2 brief drafts/month'"*. The other two changes
 * dated 2026-08-15 cite a decision Adam made (free.citations, student_ultra.citations); this one
 * cites THE PAGE. And the page did not say it first. MEASURED, not inferred:
 *
 *   git show 6292e1c94^:apps/main/public/pricing.html | grep draft   →  NOTHING.
 *   The twelve pricing.html revisions before it, back to 2026-07-06  →  NOTHING.
 *
 * The drafts row did not exist on any pricing card until commit 6292e1c94 — the very commit that
 * raised this cell — and the row it added is `{{LIMIT_STUDENT_ULTRA_DRAFTS}}`, a PLACEHOLDER
 * RESOLVED FROM THIS TABLE. So the page "sold" 2 because the table said 2, and the table said 2
 * because the page sold it. Nothing outside that commit ever asserted the number. 1 is the value
 * this table shipped with (b5040dba) and the value Adam has now ruled.
 *
 * ⭐ THE LESSON, WHICH IS BIGGER THAN THE CELL: a canonical table is only canonical if its cells
 * are sourced from OUTSIDE the surfaces that render them. A provenance comment that cites a
 * derived surface is not provenance — it is the derivation read backwards, and it is indistinguishable
 * from a real ruling once the commit is a week old. That is what made this survive a green gate for
 * thirteen days: every instrument checked CONSISTENCY, and the wrong number was perfectly consistent.
 *
 * ⚠️ NOBODY LOSES A DRAFT TO THIS. Measured against the live database on 2026-08-28 before landing:
 * no account is on student_ultra (the only non-free row in `account_allotment` is a seeded
 * attorney_ultra test user), and `usage_events` holds four `drafts` rows in total, all one user, all
 * on `free`. There is also NO persisted per-account drafts limit to strand: `account_usage` carries
 * citations_limit / searches_limit / matters_limit / checkcite_limit and no drafts column, in the
 * migration AND in the live schema — so the `GREATEST()` high-water-mark trap that stranded two rows
 * at citations_limit = 30 cannot arise for this meter. The meter reads `limitFor()` live, every call.
 *
 * ⚠️ attorney (2) AND attorney_ultra (3) ARE UNCHANGED AND WERE NOT REVIEWED. Adam named Student
 * Ultra. Their cards and this table agree, so there is nothing here to report — but "agrees" is not
 * "was ruled", and neither number has a ruling on record either.
 *
 * 🔴 THIS FILE IS NOT THE ONLY TABLE, AND IT DOES NOT YET WIN. See tableParity.test.mjs — it holds
 * the full three-table matrix and the divergences that are still open (as of 2026-08-15: free
 * citations, where both app tables still grant 30 against the 20 ruled here). Every one of them is
 * REPORTED there, not silently reconciled, because changing them changes what a paying customer is
 * entitled to.
 *
 * Consumed by CJS (apps/main) via require() and by ESM (apps/steel, packages/ui) via import — one
 * data literal, two entry points, no build step. See package.json's exports map.
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 *
 * 🔴 THE FOUR METERS ARE MONTHLY POOLS. ALL OF THEM. There is no daily cap in this table.
 * The predecessor mixed periods inside one row — `clark` was a DAILY cap in TURNS sitting beside
 * MONTHLY pools in citations/searches/matters, and the only thing distinguishing them was a comment.
 * Every number here is "per allotment period", counted as events since allotment_period_start.
 *
 *   queries    Clark AND CiteClerk Research, COMBINED. Handing state from one app to the other
 *              via the interop buttons is ONE query, not two — see meterQuery()'s `ref` de-dupe.
 *   citations  The Citations app only. BOTH citation generation and citation checks count here.
 *              Doc Checks do NOT.
 *   docChecks  Metered separately from citations. Never drawn from the citations pool.
 *   drafts     One credit per draft CREATED.
 *
 * 🔴 NOTHING IS UNLIMITED. attorney_ultra's queries used to be `null` (unbounded), against a
 * measured $0.011008/turn. A tier whose ceiling is `null` has no ceiling and cannot be forecast;
 * 50 is a number, and a number can be raised on purpose.
 */

// ── tiers ────────────────────────────────────────────────────────────────────
/** Canonical tier order, cheapest → dearest. Upgrade/downgrade direction is defined by this index. */
const TIERS = Object.freeze(['free', 'student', 'student_ultra', 'attorney', 'attorney_ultra']);

/**
 * 🔴 CLERK'S FREE PLAN SLUG IS `free_user`, NOT `free`.
 * Verified against the live instance: cplan_3EbrVJRyD8eSL7VQudMaNxDVhmp has slug "free_user".
 * The four paid slugs match our keys exactly; only free differs. The old resolvers never noticed
 * because they checked the four PAID slugs top-down and returned the literal "free" on fallthrough —
 * correct by accident. Any code that does PLAN_LIMITS[slugFromClerk] would have missed. Normalize.
 */
const CLERK_SLUG_ALIASES = Object.freeze({
  free_user: 'free',
  free: 'free',
  // Historical slugs seen in this tree. Mapped rather than dropped so an old token resolves to a
  // real tier instead of silently becoming `free` for a paying customer.
  student1: 'student',
  student2: 'student_ultra',
  attorney1: 'attorney',
  attorney2: 'attorney_ultra',
});

/** Any slug (Clerk's, ours, historical) → a canonical tier key. Unknown/absent → 'free'. */
function normalizePlan(slug) {
  if (typeof slug !== 'string' || !slug) return 'free';
  const s = slug.trim().toLowerCase();
  if (TIERS.includes(s)) return s;
  return CLERK_SLUG_ALIASES[s] || 'free';
}

// ── the table ────────────────────────────────────────────────────────────────
/**
 * Per-allotment-period pools. Every value is a non-negative integer — 0 means "not included on this
 * tier" and blocks immediately; there is deliberately no `null`/unlimited sentinel.
 *
 * Free and Student have ZERO queries BY DESIGN. Clark and Research are premium features. Unregistered
 * visitors get a separate one-time trial grant (see server/lib/anonTrial.js) which is NOT tier
 * allotment and does not appear in this table.
 */
const PLAN_LIMITS = Object.freeze({
  // citations: 10 — ADAM'S RULING 2026-08-28 (RUN XD). The BASE allotment. A free account with a
  // VERIFIED .edu gets 20 instead, and that is NOT a second row — it is CONDITIONAL_LIMITS below,
  // resolved by limitFor(). This row is what an account gets with no condition met, which is also
  // what every non-conditional reader (a projection, a test, a copy placeholder) sees by default.
  // History: 20 was ADAM'S RULING 2026-08-15, itself a deliberate reversal of 2026-08-05's 30.
  free:           Object.freeze({ queries: 0,  citations: 10,   docChecks: 0, drafts: 0 }),
  student:        Object.freeze({ queries: 0,  citations: 100,  docChecks: 0, drafts: 0 }),
  // citations: 250 — ADAM'S RULING 2026-08-15. RUN F flagged 200-vs-250 and could not resolve it;
  // both app tables and the page sold 250, so 250 is now canonical and the quarantine is closed.
  // drafts: 1 — ADAM'S RULING 2026-08-28, which REVERSES the 2026-08-15 raise to 2. See the header.
  // This is the MONTHLY POOL, burned one credit per draft created (apps/seafoam POST /api/matters),
  // NOT a per-matter ceiling (that is MATTER_LIMITS / steel's draftsPerMatter, and it is untouched).
  student_ultra:  Object.freeze({ queries: 10, citations: 250,  docChecks: 1, drafts: 1 }),
  attorney:       Object.freeze({ queries: 20, citations: 500,  docChecks: 3, drafts: 2 }),
  attorney_ultra: Object.freeze({ queries: 50, citations: 1000, docChecks: 5, drafts: 3 }),
});

/**
 * ── 🔴 CONDITIONAL ALLOTMENTS — ADAM'S RULING 2026-08-28 (RUN XD) ─────────────────────────────
 *
 * *"The Free plan grants 10 citations a month. A user with a verified `.edu` gets 20. Same plan."*
 *
 * 🔴 THIS IS A NEW SHAPE, NOT A NEW NUMBER, AND THE SHAPE IS THE POINT. Every meter above is one
 * number per plan. This is the first allotment that depends on a property of the ACCOUNT rather
 * than of the TIER. It is deliberately NOT expressed as a sixth tier, a second table, or an `if`
 * in an enforcement path:
 *
 *   · A SIXTH TIER would put a Free user on a plan they did not buy, and every ladder that walks
 *     TIERS (upgrade/downgrade direction, `nextTierWithMore`, the pricing page's five cards) would
 *     have to learn to skip it. The ruling says SAME PLAN.
 *   · A SECOND TABLE is the exact defect this package exists to end — see the header: four tables
 *     that disagreed, hand-synced by a comment. A conditional kept anywhere else would be table
 *     number five.
 *   · AN `if` AT THE GATE is how `citations` came to be resolved at seven call sites while the
 *     other three meters went through one (RUN TF). Six ceilings each deciding a condition is six
 *     chances to decide it differently.
 *
 * So the condition is DATA here, and `limitFor()` — the same function every gate already calls —
 * resolves it. A caller that passes no context gets the BASE, which is the restrictive value: a
 * reader that has not been taught about the condition under-grants, and never over-grants.
 *
 * ⚠️ A CONDITIONAL MAY ONLY RAISE. `resolve()` takes the MAX of base and conditional, so a
 * mis-entered conditional below the base cannot quietly cut an allotment the tier already sells.
 * `limits.test.mjs` asserts this of every row.
 *
 * 🔴 WHAT "VERIFIED .edu" IS, AND WHY THE CONDITION IS NAMED AND NOT INLINED. There are FOUR
 * spellings of ".edu verified" in this repo and they disagree on live data (RUN XD, Block 0):
 *
 *   1. `consumed_edu_emails` (apps/main, /api/edu/verify) — a peppered hash, one school email
 *      bound to one account for good, alumni sub-domains refused. THE BURN TABLE. 1 live verified
 *      row. This is the flow the shipped Student pane drives, and it is what
 *      `referral.cjs::hasVerifiedEdu` already reads to decide a MONEY grant.
 *   2. `user_flags.has_verified_edu` (apps/steel, eduVerify.js) — a cached "does Clerk list ANY
 *      verified .edu". Strictly weaker: no burn, no one-account binding, no alumni test. 🔴 IT IS
 *      `false` FOR THE ONE LIVE ACCOUNT THAT (1) SAYS IS VERIFIED. Measured, not supposed.
 *   3. `sessionClaims.student_verified` (main, eduGate.js) — a Clerk JWT claim. 🔴 ops/b8/VERIFY.md
 *      records it as NOT PRESENT in the dashboard JWT template, and ops/b8/jwt-gate.md: *"until
 *      this is configured, the claim is always absent."* A ceiling built on it would read FALSE for
 *      everybody and silently grant 10 to a verified student — a calm failure, the kind this repo
 *      keeps rediscovering.
 *   4. `checkEduGate` (steel) — derives from the primary email DOMAIN at read time. A user who
 *      changes their primary email changes their allotment, silently, with no burn to appeal to.
 *
 * (1) is the only one that is authoritative, burned, and already trusted with money. The condition
 * is therefore named `eduVerified` and RESOLVED BY THE CALLER — this package does no I/O and must
 * not learn what a database is. apps/main/server/lib/eduEntitlement.js is the one resolver, and it
 * reads (1). Naming the condition rather than inlining a predicate is what makes the choice of
 * source a single, reviewable decision instead of four call sites each picking a spelling.
 */
const CONDITIONS = Object.freeze(['eduVerified']);

/**
 * plan → meter → { [condition]: limit }. A meter absent here has no conditional on that plan.
 * ⚠️ ONLY `free.citations` has one. Adding a row here changes what a customer is entitled to.
 */
const CONDITIONAL_LIMITS = Object.freeze({
  free: Object.freeze({
    // 20 with a verified .edu, against a base of 10. ADAM'S RULING 2026-08-28.
    citations: Object.freeze({ eduVerified: 20 }),
  }),
});

/**
 * The conditional row for one plan+meter, or null. Exported so a COPY surface can ask "does this
 * cell have a condition, and what does it grant" without restating either number — the pricing
 * card's second line and plan-copy-gate's check are both built from this.
 */
function conditionalFor(plan, meter) {
  const row = CONDITIONAL_LIMITS[normalizePlan(plan)];
  const cell = row && Object.prototype.hasOwnProperty.call(row, meter) ? row[meter] : null;
  return cell || null;
}

/**
 * ── 🔴 THE MID-CYCLE FLOOR — NOBODY'S ALLOTMENT SHRINKS UNDERNEATH THEM ───────────────────────
 *
 * RUN XD cut the Free base from 20 to 10 on 2026-08-28, mid-period. A free account that had already
 * spent 15 of its 20 would, the instant this shipped, be 5 over a ceiling it had never been told
 * about — refused by six live gates that are NOT behind METERING_ENFORCED.
 *
 * ⭐ THE SAFE DIRECTION IS TOWARD THE CUSTOMER, and this is the same instinct RUN QB applied when
 * it moved a burn between paths: fail toward under-counting against a live paywall. So a period
 * that had ALREADY STARTED when the cut landed keeps the allotment it was sold; the new base
 * applies from the next period boundary. This is not a grandfathered ACCOUNT — it is a
 * grandfathered PERIOD, so it retires by itself on 2026-09-01 with no cleanup and no flag to
 * remember to turn off.
 *
 * A caller that passes no `periodStart` gets the new base. That is correct for every NEW period and
 * for every reader that is not deciding a live refusal (copy, the pricing card, a test) — the floor
 * is a refusal-time concession, not a thing the company is still selling.
 */
const PRIOR_LIMITS = Object.freeze({
  free: Object.freeze({ citations: 20 }),   // what Free granted before 2026-08-28
});
/** Periods starting BEFORE this date keep PRIOR_LIMITS. ISO date, compared as a string. */
const PRIOR_LIMITS_UNTIL = '2026-09-01';

/** The floor a still-open pre-cut period is owed for one plan+meter, or 0. */
function priorFloorFor(plan, meter, periodStart) {
  if (!periodStart) return 0;
  const iso = periodStart instanceof Date ? periodStart.toISOString().slice(0, 10) : String(periodStart).slice(0, 10);
  if (!(iso < PRIOR_LIMITS_UNTIL)) return 0;
  const row = PRIOR_LIMITS[normalizePlan(plan)];
  return row && Object.prototype.hasOwnProperty.call(row, meter) ? row[meter] : 0;
}

/** The meter keys, frozen. The list IS the rule — a meter not in here cannot be counted or charged. */
const METERS = Object.freeze(['queries', 'citations', 'docChecks', 'drafts']);

/** meter key → the `usage_events.meter` value stored in Postgres (snake, stable across renames). */
const METER_COLUMN = Object.freeze({
  queries: 'queries', citations: 'citations', docChecks: 'doc_checks', drafts: 'drafts',
});
/** Inverse of METER_COLUMN. */
const METER_FROM_COLUMN = Object.freeze({
  queries: 'queries', citations: 'citations', doc_checks: 'docChecks', drafts: 'drafts',
});

/**
 * 🔴 THE LEGACY `banked_credits.kind` VOCABULARY → THE CANONICAL METER. RUN F, 2026-08-12.
 *
 * `banked_credits` carries TWO columns naming the same thing: `kind` (NOT NULL, DEFAULT 'search' —
 * the Phase-3B vocabulary) and `meter` (NULLABLE — added by migration 013). The canonical read and
 * spend paths (packBalance / drawPack / returnToPack, and therefore every pack badge in every
 * shipped client) key on `meter`. But EVERY live deposit path — buy-search-pack, auto-recharge,
 * both referral rewards and promo redemption — goes through matters.js's depositBankedCredits,
 * which never wrote `meter` at all. `depositPack`, the one writer that DOES set it, has no
 * production caller.
 *
 * So the column the product reads was written by nothing, and the column everything writes is read
 * by nothing. A purchased pack was invisible in the Usage pane AND undrawable by drawPack. The
 * defect is prospective only — `banked_credits` held 0 rows when this was found, so there is no
 * backfill and nothing to migrate.
 *
 * This map is the bridge, and it lives here because a vocabulary is exactly the kind of thing that
 * must not be re-derived per call site. Unknown kinds return null — NEVER a guess and never a
 * default, because inventing a meter would silently move a customer's credit to the wrong pool.
 */
const KIND_TO_METER_COLUMN = Object.freeze({
  search: 'queries', searches: 'queries', query: 'queries', queries: 'queries',
  citation: 'citations', citations: 'citations',
  doc_check: 'doc_checks', doc_checks: 'doc_checks', docchecks: 'doc_checks',
  draft: 'drafts', drafts: 'drafts',
});

/**
 * Legacy `kind` → the `banked_credits.meter` value to store beside it.
 * Returns null for anything unrecognised, so the caller can log and store NULL rather than guess.
 */
function meterColumnForKind(kind) {
  if (typeof kind !== 'string' || !kind) return null;
  return KIND_TO_METER_COLUMN[kind.trim().toLowerCase()] || null;
}

/** Human labels for refusal copy and the usage panes. */
const METER_LABEL = Object.freeze({
  queries: 'queries', citations: 'citations', docChecks: 'Doc Checks', drafts: 'drafts',
});

const TIER_LABEL = Object.freeze({
  free: 'Free', student: 'Student', student_ultra: 'Student Ultra',
  attorney: 'Attorney', attorney_ultra: 'Attorney Ultra',
});

// ── pricing ──────────────────────────────────────────────────────────────────
/**
 * US cents. `annual` is the ANNUAL-BILLED-MONTHLY rate (what the customer sees per month on an
 * annual commitment); `monthly` is month-to-month.
 *
 * 🔴 CLERK'S LIVE STUDENT PLAN DISAGREED WITH THIS. As read from the instance on 2026-08-11,
 * Student was fee=500 / annual_monthly=400 ($5 / $4) against the $9 / $7 below. The other three
 * paid tiers matched to the cent. ops/stream-b/clerk_sync.mjs reconciles Clerk TO this table.
 */
const PRICING = Object.freeze({
  free:           Object.freeze({ annual: 0,    monthly: 0    }),
  student:        Object.freeze({ annual: 700,  monthly: 900  }),
  student_ultra:  Object.freeze({ annual: 1600, monthly: 2000 }),
  attorney:       Object.freeze({ annual: 2500, monthly: 3000 }),
  attorney_ultra: Object.freeze({ annual: 5000, monthly: 6000 }),
});

// ── à la carte packs ─────────────────────────────────────────────────────────
/**
 * UNIVERSAL ELIGIBILITY — every pack is purchasable by every user INCLUDING Free. A Free user who
 * buys a query pack can run queries even though their tier allotment is 0; that is the intent, not a
 * loophole. All packs expire ONE YEAR after purchase, each on its own clock (per-purchase expiry, not
 * a per-user one) — that is why banked_credits is one row per purchase.
 */
const PACKS = Object.freeze({
  citations: Object.freeze({ meter: 'citations', units: 100, priceCents: 1000, label: '100 citations' }),
  queries:   Object.freeze({ meter: 'queries',   units: 10,  priceCents: 1000, label: '10 queries'    }),
  docChecks: Object.freeze({ meter: 'docChecks', units: 1,   priceCents: 1000, label: '1 Doc Check'   }),
  drafts:    Object.freeze({ meter: 'drafts',    units: 1,   priceCents: 2500, label: '1 draft'       }),
});

/** Pack expiry, in ms. One year from the moment of purchase. */
const PACK_TTL_MS = 365 * 24 * 60 * 60 * 1000;

// ── matters ──────────────────────────────────────────────────────────────────
/** 🔴 FLAT, NOT TIERED. 999 for every account including Free. */
const MATTER_CAP = 999;
/** Above this many matters the picker MUST render a search field. */
const MATTER_PICKER_SEARCH_THRESHOLD = 30;

// ── per-MATTER ceilings (RUN JB, 2026-08-23) ─────────────────────────────────
/**
 * 🔴 THESE ARE NOT MONTHLY POOLS. Every row in PLAN_LIMITS above is "per allotment period, counted
 * since allotment_period_start". Every row HERE is "per matter, counted over that matter's own
 * rows", and it resets by opening a new matter — which is itself bounded by seafoam's
 * `mattersPerMonth`. The two tables are kept apart so a projection cannot mix the two periods, the
 * exact mistake this package's header records for the predecessor (`clark` was a DAILY cap in TURNS
 * beside MONTHLY pools).
 *
 * WHY THEY LIVE HERE AND NOT IN apps/seafoam/server/lib/tierUnits.js. The Draft footer said
 * `Edits · 1 of 5` and Adam clicked it at 1 of 5 and was told the limit was reached (P1-033); the
 * meter, the modal, the refusal key (`MATTER_EDITS_MAX`) and the modal's copy ("this matter's
 * assisted edits") all said PER MATTER, while the enforcing query counted PER USER PER MONTH
 * (`draft_edit_events WHERE user_id = $1 AND created_at >= date_trunc('month', now())`). Four
 * surfaces agreed with each other and disagreed with the gate. RUN GY1's rule applies: the app
 * table PROJECTS this row (tierUnits.js `editsPerMatter` / `pages` are `c.clarkEdits` / `c.pages`),
 * so the number the meter shows, the number the modal quotes and the number the gate refuses at are
 * one literal — here.
 *
 *   clarkEdits  Clark edit operations on ONE matter (highlight-to-edit, rail edits — every
 *               `draft_edit_events` row for the matter). ADAM'S RULING 2026-08-24 (RUN JB brief):
 *               free 5 → 10, renamed "Clark edits", scoped per matter. 🔴 ADAM'S RULING 2026-08-24
 *               (RUN JN): "Edits in doc should be 10 per. Queries are separate and are per month
 *               across Research and Clark." — 10 PER MATTER ON EVERY TIER, FLAT. RUN JB had shipped
 *               it tiered (25 / 100 / null / null); attorney + attorney_ultra thereby GAIN a
 *               ceiling they never had, and render MATTER_EDITS_MAX copy for the first time.
 *               Worst-case per-matter exposure is now the same on every tier (10 edits); JB's
 *               student_ultra monthly figure (100 × 8 matters) no longer exists.
 *   pages       🔴 REMOVED — ADAM'S RULING 2026-08-25 (RUN MC). There is no per-matter source-page
 *               ceiling on any plan any more. The row is deleted from this table rather than set to
 *               `null`, because `null` here means "no per-matter ceiling on THAT TIER" and seafoam's
 *               `capped()` resolves it to the ABSOLUTE env cap — which would have left a 1,000-page
 *               wall standing behind a table that claimed to have removed one. The projection in
 *               apps/seafoam/server/lib/tierUnits.js, the `checkMatterPages` gate in
 *               server/lib/limits.js, its two call sites in routes/matterSources.js, the `pages`
 *               field on GET /:id/usage and the "Source pages" rail row all came out with it.
 *               ⚠️ NOT to be confused with EXHIBIT pages (1,000, flat, ALIVE and enforced) or with
 *               the draft's own length, which is bounded by the court rule (pageBound.js) and never
 *               was a plan limit.
 *
 * `null` = no per-matter ceiling on that tier (seafoam's own spelling of unlimited, tierUnits.js).
 * It is allowed HERE and not in PLAN_LIMITS because a per-matter count on a paid tier is bounded by
 * the tier's mattersPerMonth and the matter tripwire, whereas an unbounded MONTHLY pool is not.
 * (No row uses it for clarkEdits since RUN JN; the projection and its tests still accept it.)
 */
const MATTER_LIMITS = Object.freeze({
  free:           Object.freeze({ clarkEdits: 10 }),
  student:        Object.freeze({ clarkEdits: 10 }),
  student_ultra:  Object.freeze({ clarkEdits: 10 }),
  attorney:       Object.freeze({ clarkEdits: 10 }),
  attorney_ultra: Object.freeze({ clarkEdits: 10 }),
});

/** The per-matter ceiling for one unit on one plan. Unknown plan → free; unknown unit → 0 (blocks). */
function matterLimitFor(plan, unit) {
  const row = MATTER_LIMITS[normalizePlan(plan)] || MATTER_LIMITS.free;
  return Object.prototype.hasOwnProperty.call(row, unit) ? row[unit] : 0;
}

// ── helpers ──────────────────────────────────────────────────────────────────
/**
 * The BASE row for a plan slug. Always a full frozen row; unknown slug → free (most restrictive).
 * ⚠️ This is the UNCONDITIONAL table. It is what a projection copies and what copy renders. To
 * resolve what a particular ACCOUNT is entitled to, use `limitsFor(plan, ctx)` / `limitFor(...)`.
 */
function baseLimitsFor(plan) {
  return PLAN_LIMITS[normalizePlan(plan)] || PLAN_LIMITS.free;
}

/**
 * ── THE ONE RESOLVER. base → conditional → prior-period floor. ────────────────────────────────
 *
 * `ctx` is optional and every field in it is optional:
 *   { eduVerified: boolean, periodStart: Date|'YYYY-MM-DD' }
 *
 * 🔴 THE ORDER IS FIXED AND EVERY STEP CAN ONLY RAISE. base, then MAX with any met conditional,
 * then MAX with any owed prior-period floor. A caller that passes nothing gets the base, which is
 * the most restrictive value — so a reader that has not been taught about a condition UNDER-grants.
 * That direction is deliberate: an under-grant is visible to the customer and gets reported; an
 * over-grant is invisible and gets billed.
 */
function resolveLimit(plan, meter, base, ctx) {
  let out = base;
  if (ctx) {
    const cond = conditionalFor(plan, meter);
    if (cond) {
      for (const name of CONDITIONS) {
        if (ctx[name] && Object.prototype.hasOwnProperty.call(cond, name)) out = Math.max(out, cond[name]);
      }
    }
    if (ctx.periodStart) out = Math.max(out, priorFloorFor(plan, meter, ctx.periodStart));
  }
  return out;
}

/** Limits for a plan slug, with any conditionals in `ctx` applied. Full frozen row. */
function limitsFor(plan, ctx = null) {
  const row = baseLimitsFor(plan);
  if (!ctx) return row;
  const out = {};
  for (const m of METERS) out[m] = resolveLimit(plan, m, row[m], ctx);
  return Object.freeze(out);
}

/** The limit for one meter on one plan. Unknown meter → 0 (blocks), never Infinity. */
function limitFor(plan, meter, ctx = null) {
  const row = baseLimitsFor(plan);
  const base = Object.prototype.hasOwnProperty.call(row, meter) ? row[meter] : 0;
  // An unknown meter is 0 and STAYS 0 — a conditional cannot conjure a meter the table does not have.
  if (base === 0 && !Object.prototype.hasOwnProperty.call(row, meter)) return 0;
  return resolveLimit(plan, meter, base, ctx);
}

/**
 * 🔴 THE CARRYOVER RULE, IN ONE FUNCTION. `new_tier_limit − usage_since_allotment_period_start`,
 * FLOORED AT ZERO.
 *
 * This is what makes a mid-cycle upgrade correct without any special handling: usage is NOT forgiven,
 * so a Student who burned all 100 citations and upgrades to Attorney (500) has 400 left, not 500.
 * Because allotment_period_start does not move on an upgrade, a SECOND upgrade in the same cycle
 * accumulates against the same denominator and this same expression stays right — there is no
 * "carried usage" field to maintain and nothing to reconcile.
 *
 * The floor matters on a DOWNGRADE that has already taken effect (or a plan whose new ceiling is
 * below what was already spent): remaining is 0, never negative. A negative remaining rendered in a
 * meter reads as a bug and, worse, `used < limit` arithmetic elsewhere can flip sign on it.
 */
function remainingFor(plan, meter, usedSincePeriodStart, ctx = null) {
  const limit = limitFor(plan, meter, ctx);
  const used = Number(usedSincePeriodStart) || 0;
  return Math.max(0, limit - used);
}

/** Position in the tier ladder. -1 for unknown. Used to classify a plan change. */
function tierRank(plan) {
  return TIERS.indexOf(normalizePlan(plan));
}

/** 'upgrade' | 'downgrade' | 'none'. Compares by ladder position, not by price. */
function planChangeKind(fromPlan, toPlan) {
  const a = tierRank(fromPlan), b = tierRank(toPlan);
  if (a === b) return 'none';
  return b > a ? 'upgrade' : 'downgrade';
}

/** Monthly price in cents for a plan on a given cadence ('annual' | 'monthly'). */
function priceCents(plan, cadence = 'monthly') {
  const row = PRICING[normalizePlan(plan)] || PRICING.free;
  return cadence === 'annual' ? row.annual : row.monthly;
}

module.exports = {
  TIERS, TIER_LABEL, CLERK_SLUG_ALIASES, normalizePlan,
  PLAN_LIMITS, METERS, METER_COLUMN, METER_FROM_COLUMN, METER_LABEL,
  CONDITIONS, CONDITIONAL_LIMITS, conditionalFor,
  PRIOR_LIMITS, PRIOR_LIMITS_UNTIL, priorFloorFor,
  baseLimitsFor,
  KIND_TO_METER_COLUMN, meterColumnForKind,
  PRICING, priceCents,
  PACKS, PACK_TTL_MS,
  MATTER_CAP, MATTER_PICKER_SEARCH_THRESHOLD,
  MATTER_LIMITS, matterLimitFor,
  limitsFor, limitFor, remainingFor, tierRank, planChangeKind,
};
